import { useEffect, useState } from "react";
import Parse from "parse";
import { useGame } from "../header/GameContext";
import ButtonOnly from "../button/buttonOnly";
import InputText from "../inputText/inputText";
import { saveToSheet } from "../../api/sheet";
import "./login.css";

/* ================================================================
   설정값 — 변경 시 여기만 수정
   ================================================================ */
const GAME_TABLE = "silence1"; // Back4App 테이블명
const PROG_PREFIX = "op"; // 입장 코드 접두어 (opa001 → "op")
const COURSE_CLASS = "CourseList";
const DEFAULT_TIME = 3600; // CourseList 조회 실패 시 기본값(초)

/* ================================================================
   유틸
   ================================================================ */
const getRoundFromCode = (inCode, prefix) => {
  if (!inCode || !prefix) return null;
  const alpha = inCode.slice(prefix.length, prefix.length + 1);
  const round = alpha.charCodeAt(0) - 96; // a=1, b=2 ...
  return round > 0 ? round : null;
};

const todayKST = () => {
  const kst = new Date(Date.now() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 10);
};

/* ================================================================
   CourseList에서 게임시간 조회
   ================================================================ */
const fetchGameTime = async (inCode) => {
  try {
    const round = getRoundFromCode(inCode, PROG_PREFIX);
    const today = todayKST();
    console.log("⏱ 게임시간 조회:", { inCode, round, today });

    if (round === null) {
      console.warn("⚠ 회차 추출 실패 → 기본값");
      return DEFAULT_TIME;
    }

    const q = new Parse.Query(COURSE_CLASS);
    q.equalTo("prefix", PROG_PREFIX);
    q.equalTo("round", round);
    q.equalTo("useDate", today);
    q.descending("createdAt");
    const course = await q.first();

    if (course) {
      const sec = course.get("duration") * 60;
      console.log(`✅ 게임시간: ${course.get("duration")}분 → ${sec}초`);
      return sec;
    }
    console.warn("⚠ CourseList 과정 없음 → 기본값");
    return DEFAULT_TIME;
  } catch (e) {
    console.warn("게임시간 조회 실패:", e.message);
    return DEFAULT_TIME;
  }
};

/* ================================================================
   LoginName
   ================================================================ */
function LoginName({ handleName, setHandleName, onFinish, defaultPath }) {
  const {
    playerKeyCode,
    setPlayerKeyCode,
    setPlayerName,
    pauseTimer,
    introTeamName,
    introInCodeV,
    syncTimer,
    setClearedStages,
  } = useGame();

  const teamName = introTeamName || localStorage.getItem("INTRO_TEAM") || "";
  const inCode = introInCodeV || localStorage.getItem("INTRO_CODE") || "";
  const keyCode =
    playerKeyCode || localStorage.getItem("PLAYER_KEY_CODE") || "";

  const safeName = handleName || "";
  const isActive = safeName.trim().length > 0;
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    pauseTimer();
  }, [pauseTimer]);

  const handleStart = async () => {
    const trimmedName = safeName.trim();
    if (!trimmedName) {
      alert("이름(닉네임) 입력해주세요.");
      return;
    }
    if (!teamName || !keyCode) {
      alert("잘못된 접근입니다. 처음부터 다시 진행해주세요.");
      return;
    }

    setIsLoading(true);

    try {
      const GameUser = Parse.Object.extend(GAME_TABLE);

      /* ── 1. 이어하기: 팀명 + 입장코드 + 닉네임 모두 일치 ── */
      const continueQuery = new Parse.Query(GameUser);
      continueQuery.equalTo("introTeam", teamName);
      continueQuery.equalTo("introInCode", inCode);
      continueQuery.equalTo("playerName", trimmedName);
      const existingUser = await continueQuery.first();

      if (existingUser) {
        // CourseList에서 최신 게임시간 조회
        const latestTime = await fetchGameTime(inCode);
        const savedTime = existingUser.get("timeRemaining");
        const restoreTime =
          savedTime && savedTime < latestTime ? savedTime : latestTime;

        localStorage.setItem("ADMIN_TIME_KEY", String(latestTime));
        localStorage.setItem("GAME_TIME_REMAIN", String(restoreTime));
        syncTimer(restoreTime);
        console.log(
          "✅ 이어하기 - 관리자설정:",
          latestTime,
          "/ 복원시간:",
          restoreTime,
        );

        // 단서 복원
        const savedStages = existingUser.get("clearedStages");
        if (savedStages && Array.isArray(savedStages)) {
          localStorage.setItem("clearedStages", JSON.stringify(savedStages));
          setClearedStages(new Set(savedStages));
          console.log("✅ 이어하기 - 단서 복원:", savedStages);
        }

        // keyCode 복원
        const savedKeyCode = existingUser.get("keyCode") || keyCode;
        if (savedKeyCode) {
          localStorage.setItem("PLAYER_KEY_CODE", savedKeyCode);
          setPlayerKeyCode(savedKeyCode);
        }

        localStorage.setItem("gameUserId", existingUser.id);
        setPlayerName(trimmedName);

        onFinish?.({
          defaultPath: "/keywordInput",
          state: { isContinue: true },
        });
        return;
      }

      /* ── 2. 닉네임 중복 체크 ── */
      const nameQuery = new Parse.Query(GameUser);
      nameQuery.equalTo("playerName", trimmedName);
      const sameNameUser = await nameQuery.first();

      if (sameNameUser) {
        alert("이미 사용중인 닉네임입니다.");
        setIsLoading(false);
        return;
      }

      /* ── 3. CourseList에서 게임시간 조회 ── */
      const adminTime = await fetchGameTime(inCode);

      /* ── 4. 신규 생성 ── */
      const newUser = new GameUser();
      newUser.set("playerName", trimmedName);
      newUser.set("introTeam", teamName);
      newUser.set("keyCode", keyCode);
      newUser.set("introInCode", inCode);
      newUser.set("clearedStages", []);
      newUser.set("timeRemaining", adminTime);
      await newUser.save();

      // ✅ GameContext 타이머 즉시 반영
      syncTimer(adminTime);
      localStorage.setItem("ADMIN_TIME_KEY", String(adminTime));
      localStorage.setItem("GAME_TIME_REMAIN", String(adminTime));
      localStorage.setItem("gameUserId", newUser.id);
      setPlayerName(trimmedName);

      console.log("✅ 신규 생성 완료 - ID:", newUser.id, "시간:", adminTime);

      /* ── 5. Google Sheet ── */
      try {
        await saveToSheet({ keyCode, playerName: trimmedName });
      } catch (sheetError) {
        console.error("시트 저장 실패:", sheetError.message);
      }

      onFinish?.({
        defaultPath,
        state: { DateVal: "DATA1", titImgClass: "infoTit" },
      });
    } catch (e) {
      console.error(e);
      alert("로그인 저장 실패");
      setIsLoading(false);
    }
  };

  return (
    <div id="content">
      <div className="box2">
        <div id="box_con" className="main_box2">
          <div className="main_Bimg"></div>
          <div className="pdLR16">
            <InputText
              textClass="input02 mt16"
              textValue="이름을 입력하세요."
              handleName={handleName}
              setHandleName={setHandleName}
            />
            <ButtonOnly
              btnValue={
                isLoading ? (
                  <>
                    <span className="btn-spinner white" />
                  </>
                ) : (
                  "START"
                )
              }
              btnClass={`inputBtn01 mt16 ${isActive ? "active" : ""}`}
              onFinish={handleStart}
              disabled={!isActive || isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginName;
